// context/AppUserContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Permission, User as AppUser } from '../api/models/user';
import { getPermissions } from '../api/userApi';

interface AppUserContextProps {
	appUser: AppUser | null;
	getPermission: (orgSeasonGuid: string) => Permission['access'] | null;
	isAdmin: (orgSeasonGuid: string) => boolean;
	refreshAppUser: () => Promise<void>;
}

const AppUserContext = createContext<AppUserContextProps | undefined>(undefined);

const STORAGE_KEY = 'appUserData';

export const AppUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
	const [appUser, setAppUser] = useState<AppUser | null>(null);

	// Load from sessionStorage on init
	useEffect(() => {
		console.log('useEffect - no dep - get cached item');
		try {
			const cachedData = sessionStorage.getItem(STORAGE_KEY);
			if (cachedData) {
				console.log('Loading AppUser from sessionStorage');

				const parsed: AppUser = JSON.parse(cachedData);
				console.log('setting appUser from cache');
				setAppUser(parsed);
			} else {
				//note: In Safari private mode, sessionStorage is not available
				console.log('No cached AppUser found');
			}
		} catch {
			sessionStorage.removeItem(STORAGE_KEY);
		}
	}, []);

	// Fetch AppUser (called on first load or refresh)
	const fetchAppUser = useCallback(async () => {
		console.log('fetchAppUser');

		if (!isAuthenticated) return;
		try {
			const accessToken = await getAccessTokenSilently({ audience: `${process.env.REACT_APP_AUTH0_AUDIENCE}` });
			const response = await getPermissions(accessToken);
			console.log('Fetched AppUser:', response);

			if (!response) {
				throw new Error('Failed to fetch AppUser');
			}

			const data = response as AppUser;
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
			setAppUser(data);
		} catch (error) {
			//note: In Safari private mode, sessionStorage is not available
			console.error('Error fetching AppUser:', error);
		}
	}, [isAuthenticated, getAccessTokenSilently]);

	// Auto-fetch if authenticated and no cached user
	useEffect(() => {
		console.log('useEffect - deps[isAuth,appUser,fetchAppUser]');

		if (isAuthenticated && !appUser) {
			fetchAppUser();
		}
	}, [isAuthenticated, appUser, fetchAppUser]);

	// Clear sessionStorage on logout
	const handleLogout = useCallback(() => {
		console.log('Logging out and clearing sessionStorage');

		sessionStorage.removeItem(STORAGE_KEY);
		setAppUser(null);
		logout({ returnTo: window.location.origin });
	}, [logout]);

	// Permission helper
	const getPermission = useCallback(
		(orgSeasonGuid: string): Permission['access'] | null => {
			if (!appUser || !appUser.permissions) {
				return null;
			}

			if (appUser?.isSuperAdmin) {
				return 'superadmin';
			}
			console.log(`requesting permission for orgSeasonGuid: ${orgSeasonGuid} - which might be orgId?`);
			const permission = appUser.permissions.find((r) => r.orgId === orgSeasonGuid);
			console.log(`found permission: ${JSON.stringify(permission)}`);
			return permission ? permission.access : null;
		},
		[appUser]
	);

	const isAdmin = (orgSeasonGuid: string): boolean => {
		console.log(`appUser: ${JSON.stringify(appUser)}`);

		if (appUser?.isSuperAdmin) {
			return true;
		}

		const access = getPermission(orgSeasonGuid);
		return access === 'admin' || access === 'superAdmin';
	};

	return (
		<AppUserContext.Provider
			value={{
				appUser,
				getPermission,
				isAdmin,
				refreshAppUser: fetchAppUser,
			}}
		>
			{children}
		</AppUserContext.Provider>
	);
};

//Custom hook to use the context
export const useAppUser = () => {
	const context = useContext(AppUserContext);
	if (!context) {
		throw new Error('useAppUser must be used within AppUserProvider');
	}
	return context;
};
